import { useState, createContext, useContext, useEffect, FC } from 'react';
import GlobalContext from './GlobalContext';
import API from '../api/api';
import { useLocation } from 'react-router';
import { EConnectBaseUrl, EQueryStringParam, EPromiseResolution, EIdentityClientId } from '../util/constants';
import { TBusiness, TUser } from '../util/types';
import { parseUserBusinesses } from '../util/helpers';
import { TRequestVerifyPost } from '../api/types';

type TVerifyAccountContext = {
    verifyAccountUiState: EVerifyAccountUiState;
    businesses: TBusiness[];
    primaryUser: TUser;
    hasFamilyUsers: boolean;
    legacySyncToken: string;
    connectBaseUrl: EConnectBaseUrl;
    sendVerificationEmail: () => Promise<void>;
    showToaster: boolean;
    toggleToaster: () => void;
};

export enum EVerifyAccountUiState {
    default,
    initialized,
    loginLinkParsed,
    dataLoaded,
    ready,
    error,
    fatalError,
    success,
}

const VerifyAccountContext = createContext<TVerifyAccountContext | undefined>(undefined);

export const VerifyAccountProvider: FC = ({ children }) => {
    const {
        connectBaseUrl,
        gatewayAPIBaseUrl,
        getFullLoginLinkFromLocalStorage,
        fullLoginLink,
        subscriberId,
        setSubscriberId,
        setIdentityClientId,
        getCoBranding,
    } = useContext(GlobalContext);
    const [verifyAccountUiState, setVerifyAccountUiState] = useState(EVerifyAccountUiState.default);
    const [userId, setUserId] = useState('');
    const [businesses, setBusinesses] = useState<TBusiness[]>([]);
    const [legacySyncToken, setLegacySyncToken] = useState('');
    const [primaryUser, setPrimaryUser] = useState<TUser>({ firstName: '', lastName: '', id: '' });
    const [hasFamilyUsers, setHasFamilyUsers] = useState(false);
    const [showToaster, setShowToaster] = useState(false);
    const { search: queryParams } = useLocation();

    // ---------
    // Setup
    // ---------
    const parseUrl = () => {
        const params = new URLSearchParams(queryParams);
        const subscriberId = params.get(EQueryStringParam.subscriberId);
        const userId = params.get(EQueryStringParam.userId);
        subscriberId && setSubscriberId(subscriberId);
        userId && setUserId(userId);
    };

    const initialize = () => {
        getFullLoginLinkFromLocalStorage();
        parseUrl();
        setVerifyAccountUiState(EVerifyAccountUiState.initialized);
    };

    const parseLoginLink = () => {
        if (fullLoginLink) {
            const clientId = new URLSearchParams(fullLoginLink.split('?')[1]).get(
                EQueryStringParam.clientId,
            ) as EIdentityClientId;
            clientId && setIdentityClientId(clientId);
        }
        setVerifyAccountUiState(EVerifyAccountUiState.loginLinkParsed);
    };

    const verifyAccount = async () => {
        try {
            const response = await API.get.verifyAccount(queryParams);
            const businesses: TBusiness[] = response.data.userBusinesses.map((business) => ({
                users: business.users,
                name: business.subscriberName,
                city: business.city,
                stateOrProvince: business.stateOrProvinceCode,
                countryCode: business.countryCode,
                subscriberId: business.subscriberId,
            }));
            setBusinesses(businesses);
            setLegacySyncToken(response.data.legacyManualSyncToken);
        } catch (error) {
            setVerifyAccountUiState(EVerifyAccountUiState.error);
            throw Error();
        }
    };

    const getInitialData = async () => {
        const [_, verificationResponse] = await Promise.allSettled([getCoBranding(), verifyAccount()]);
        if (verificationResponse.status === EPromiseResolution.fulfilled) {
            setVerifyAccountUiState(EVerifyAccountUiState.dataLoaded);
        }
    };

    useEffect(() => {
        switch (verifyAccountUiState) {
            case EVerifyAccountUiState.default:
                initialize();
                break;
            case EVerifyAccountUiState.initialized:
                parseLoginLink();
                break;
            case EVerifyAccountUiState.loginLinkParsed:
                getInitialData();
                break;
            case EVerifyAccountUiState.dataLoaded:
                const parsedBusinesses = parseUserBusinesses({
                    businesses,
                    primaryUserId: userId,
                });
                setPrimaryUser(parsedBusinesses.primaryUser);
                setBusinesses(parsedBusinesses.businesses);
                setHasFamilyUsers(parsedBusinesses.hasFamilyUsers);
                setVerifyAccountUiState(EVerifyAccountUiState.ready);
                break;
            default:
                break;
        }
    }, [verifyAccountUiState]);

    // ----------
    // Util
    // ----------
    const toggleToaster = () => {
        setShowToaster(true);
        setTimeout(() => {
            setShowToaster(false);
        }, 5000);
    };

    const sendVerificationEmail = async () => {
        const postObject: TRequestVerifyPost = { userId, ...(subscriberId && { subscriberId }) };
        try {
            await API.post.requestVerify(gatewayAPIBaseUrl, postObject);
            toggleToaster();
        } catch {
            console.error('Failed to send verification email');
            setVerifyAccountUiState(EVerifyAccountUiState.fatalError);
        }
    };

    // -------------
    // Context output
    // -------------
    return (
        <VerifyAccountContext.Provider
            value={{
                verifyAccountUiState,
                businesses,
                primaryUser,
                hasFamilyUsers,
                legacySyncToken,
                connectBaseUrl,
                sendVerificationEmail,
                showToaster,
                toggleToaster,
            }}
        >
            {children}
        </VerifyAccountContext.Provider>
    );
};

export default VerifyAccountContext;
